/**
 * Shared filter state hook for scouting filter surfaces
 *
 * Extracts all filter state (positions, clubs, scouts, nationalities,
 * leagues, roles, and 12 range filters) into a dedicated hook shared
 * by both the Add Player modal and the Scouting page FilterSheet.
 * Also fetches filter options from dedicated list queries (positions,
 * clubs, leagues, nationalities) so lookups are stable regardless of
 * search results — preventing UUID flashes in filter chips.
 * Clubs use broader queries (eligible teams) with server-side search
 * support via setClubSearchText so users can search across all available
 * options. Leagues use org-scoped curated list via useOrganizationLeagues.
 * State resets on modal close (via clearAllFilters) and on component
 * unmount (page refresh). Exports FilterSnapshot type, takeSnapshot,
 * DEFAULT_FILTER_SNAPSHOT, and restoreSnapshot for draft filter support.
 * @module features/scouting/hooks/useFilterState
 */

import { useCallback, useLayoutEffect, useMemo, useState } from "react";

import {
  DEFAULT_AGE_RANGE,
  DEFAULT_CONTRACT_MONTHS_RANGE,
  DEFAULT_CUSTOM_STAT_RANGE,
  DEFAULT_SCORE_RANGE,
  DEFAULT_VALUATION_RANGE,
  isRangeActive,
} from "../constants/filterDefaults";

import type { FilterOption } from "@/types/filter";

import {
  useRosterManagementListMyOrganizationsEligibleTeamsQuery,
  useRosterManagementListNationalitiesQuery,
  useRosterManagementListPositionsQuery,
  useScoutingListRoleArchetypesQuery,
  useScoutingListScoutsQuery,
} from "@/generated/graphql";
import { POSITIONS_FULL_LIST_FIRST } from "@/config/constants";
import { useAuth } from "@/features/auth";
import useOrganizationLeagues from "@/hooks/useOrganizationLeagues";
import dedupePositionNodes from "@/utils/dedupePositionNodes";
import useCompetitionOptions from "@/hooks/useCompetitionOptions";
import type { LeaguePresetOption } from "@/features/player-filter/utils/leaguePresets";
import {
  buildNationalityMemberNamesById,
  groupNationalitiesByCountry,
} from "@/utils/nationalityCountryGroups";

/**
 * Counts truthy predicates — used for filter count badges
 * @param predicates - Boolean conditions to count
 * @returns Number of truthy values
 */
const countActive = (...predicates: readonly boolean[]): number =>
  predicates.filter(Boolean).length;

/** Snapshot of all filter values — used for draft/commit and restore */
export type FilterSnapshot = {
  readonly selectedPositionIds: readonly string[];
  readonly selectedClubIds: readonly string[];
  readonly selectedScoutIds: readonly string[];
  readonly selectedNationalityIds: readonly string[];
  readonly selectedLeagueIds: readonly string[];
  readonly selectedCompetitionIds: readonly string[];
  readonly selectedRoleArchetypes: readonly string[];
  readonly ageRange: [number, number];
  readonly contractMonthsRange: [number, number];
  readonly valuationRange: [number, number];
  readonly gprRange: [number, number];
  readonly fitScoreRange: [number, number];
  readonly teamStyleFitRange: [number, number];
  readonly carryingRange: [number, number];
  readonly dribblingRange: [number, number];
  readonly passingRange: [number, number];
  readonly shootingRange: [number, number];
  readonly gkPassingRange: [number, number];
  readonly gkShotStoppingRange: [number, number];
  readonly gkPositioningRange: [number, number];
  readonly customStatRanges: Readonly<
    Record<string, readonly [number, number]>
  >;
};

/**
 * Captures all filter values from a FilterState into a snapshot
 * @param state - Source filter state
 * @returns Immutable snapshot of all filter values
 */
export const takeSnapshot = (state: FilterState): FilterSnapshot => ({
  selectedPositionIds: state.selectedPositionIds,
  selectedClubIds: state.selectedClubIds,
  selectedScoutIds: state.selectedScoutIds,
  selectedNationalityIds: state.selectedNationalityIds,
  selectedLeagueIds: state.selectedLeagueIds,
  selectedCompetitionIds: state.selectedCompetitionIds,
  selectedRoleArchetypes: state.selectedRoleArchetypes,
  ageRange: state.ageRange,
  contractMonthsRange: state.contractMonthsRange,
  valuationRange: state.valuationRange,
  gprRange: state.gprRange,
  fitScoreRange: state.fitScoreRange,
  teamStyleFitRange: state.teamStyleFitRange,
  carryingRange: state.carryingRange,
  dribblingRange: state.dribblingRange,
  passingRange: state.passingRange,
  shootingRange: state.shootingRange,
  gkPassingRange: state.gkPassingRange,
  gkShotStoppingRange: state.gkShotStoppingRange,
  gkPositioningRange: state.gkPositioningRange,
  customStatRanges: state.customStatRanges,
});

/** Default snapshot with all filters at initial values */
export const DEFAULT_FILTER_SNAPSHOT: FilterSnapshot = {
  selectedPositionIds: [],
  selectedClubIds: [],
  selectedScoutIds: [],
  selectedNationalityIds: [],
  selectedLeagueIds: [],
  selectedCompetitionIds: [],
  selectedRoleArchetypes: [],
  ageRange: DEFAULT_AGE_RANGE,
  contractMonthsRange: DEFAULT_CONTRACT_MONTHS_RANGE,
  valuationRange: DEFAULT_VALUATION_RANGE,
  gprRange: DEFAULT_SCORE_RANGE,
  fitScoreRange: DEFAULT_SCORE_RANGE,
  teamStyleFitRange: DEFAULT_SCORE_RANGE,
  carryingRange: DEFAULT_SCORE_RANGE,
  dribblingRange: DEFAULT_SCORE_RANGE,
  passingRange: DEFAULT_SCORE_RANGE,
  shootingRange: DEFAULT_SCORE_RANGE,
  gkPassingRange: DEFAULT_SCORE_RANGE,
  gkShotStoppingRange: DEFAULT_SCORE_RANGE,
  gkPositioningRange: DEFAULT_SCORE_RANGE,
  customStatRanges: {},
};

/** Return type for the useFilterState hook */
export interface FilterState {
  /* --- ID filters --- */
  readonly selectedPositionIds: readonly string[];
  readonly setSelectedPositionIds: (ids: readonly string[]) => void;
  readonly selectedClubIds: readonly string[];
  readonly setSelectedClubIds: (ids: readonly string[]) => void;
  readonly selectedScoutIds: readonly string[];
  readonly setSelectedScoutIds: (ids: readonly string[]) => void;
  readonly selectedNationalityIds: readonly string[];
  readonly setSelectedNationalityIds: (ids: readonly string[]) => void;
  readonly selectedLeagueIds: readonly string[];
  readonly setSelectedLeagueIds: (ids: readonly string[]) => void;
  readonly selectedCompetitionIds: readonly string[];
  readonly setSelectedCompetitionIds: (ids: readonly string[]) => void;
  readonly selectedRoleArchetypes: readonly string[];
  readonly setSelectedRoleArchetypes: (ids: readonly string[]) => void;

  /* --- Range filters --- */
  readonly ageRange: [number, number];
  readonly setAgeRange: (range: [number, number]) => void;
  readonly contractMonthsRange: [number, number];
  readonly setContractMonthsRange: (range: [number, number]) => void;
  readonly valuationRange: [number, number];
  readonly setValuationRange: (range: [number, number]) => void;
  readonly gprRange: [number, number];
  readonly setGprRange: (range: [number, number]) => void;
  readonly fitScoreRange: [number, number];
  readonly setFitScoreRange: (range: [number, number]) => void;
  readonly teamStyleFitRange: [number, number];
  readonly setTeamStyleFitRange: (range: [number, number]) => void;
  readonly carryingRange: [number, number];
  readonly setCarryingRange: (range: [number, number]) => void;
  readonly dribblingRange: [number, number];
  readonly setDribblingRange: (range: [number, number]) => void;
  readonly passingRange: [number, number];
  readonly setPassingRange: (range: [number, number]) => void;
  readonly shootingRange: [number, number];
  readonly setShootingRange: (range: [number, number]) => void;
  readonly gkPassingRange: [number, number];
  readonly setGkPassingRange: (range: [number, number]) => void;
  readonly gkShotStoppingRange: [number, number];
  readonly setGkShotStoppingRange: (range: [number, number]) => void;
  readonly gkPositioningRange: [number, number];
  readonly setGkPositioningRange: (range: [number, number]) => void;

  /* --- Custom stat range filters (scout report ratings, 0-10 scale) --- */
  readonly customStatRanges: Readonly<
    Record<string, readonly [number, number]>
  >;
  readonly setCustomStatRange: (name: string, range: [number, number]) => void;

  /* --- Filter sheet toggle --- */
  readonly isFilterSheetOpen: boolean;
  readonly toggleFilterSheet: () => void;
  readonly closeFilterSheet: () => void;

  /* --- Derived values --- */
  readonly activeFilterCount: number;
  readonly clearAllFilters: () => void;
  readonly restoreSnapshot: (snapshot: FilterSnapshot) => void;

  /* --- Filter options from queries --- */
  readonly scoutOptions: readonly FilterOption[];
  readonly roleOptions: readonly FilterOption[];
  readonly positionOptions: readonly FilterOption[];
  readonly clubOptions: readonly FilterOption[];
  readonly leagueOptions: readonly LeaguePresetOption[];
  readonly competitionOptions: readonly FilterOption[];
  /** True until every competition taxonomy page has been loaded. */
  readonly competitionOptionsLoading?: boolean;
  /** One option per country, not per dual-nationality combination (SE-8325) */
  readonly nationalityOptions: readonly FilterOption[];
  /**
   * Every underlying `Nationality` entity name a selected country id should
   * match, keyed by that id — expands a single country selection to also
   * match dual-national players (SE-8325).
   */
  readonly nationalityMemberNamesById: Readonly<
    Record<string, readonly string[]>
  >;

  /* --- Server-side search for large option lists --- */
  readonly setClubSearchText: (text: string) => void;

  /** True while the club search query is re-fetching */
  readonly clubSearching: boolean;

  /** True while any initial filter option query is still loading */
  readonly optionsLoading: boolean;
}

/**
 * Manages all filter state for scouting filter surfaces.
 * Owns ID filters, range filters, scout/role option queries,
 * active count derivation, and the filter sheet toggle.
 * State persists across open/close cycles.
 * @param availableMetricIds - IDs of currently available org-level custom metrics;
 *   only ranges whose key exists in this set are counted as active, preventing
 *   orphaned ranges (from deleted/renamed metrics) from inflating the badge count
 * @param optionsSource - Local candidate projections supply options without taxonomy queries.
 * @returns Filter state, setters, derived counts, and option lists
 */
const useFilterState = (
  availableMetricIds: ReadonlySet<string> = new Set(),
  optionsSource: "remote" | "local" = "remote"
): FilterState => {
  const auth = useAuth();
  const hasAuth = Boolean(auth?.data?.accessToken);
  const skipAll = !hasAuth || optionsSource === "local";

  /* --- ID filter state --- */
  const [selectedPositionIds, setSelectedPositionIds] = useState<
    readonly string[]
  >([]);
  const [selectedClubIds, setSelectedClubIds] = useState<readonly string[]>([]);
  const [selectedScoutIds, setSelectedScoutIds] = useState<readonly string[]>(
    []
  );
  const [selectedNationalityIds, setSelectedNationalityIds] = useState<
    readonly string[]
  >([]);
  const [selectedLeagueIds, setSelectedLeagueIds] = useState<readonly string[]>(
    []
  );
  const [selectedCompetitionIds, setSelectedCompetitionIds] = useState<
    readonly string[]
  >([]);
  const [selectedRoleArchetypes, setSelectedRoleArchetypes] = useState<
    readonly string[]
  >([]);

  /* --- Range filter state --- */
  const [ageRange, setAgeRange] = useState<[number, number]>(DEFAULT_AGE_RANGE);
  const [contractMonthsRange, setContractMonthsRange] = useState<
    [number, number]
  >(DEFAULT_CONTRACT_MONTHS_RANGE);
  const [valuationRange, setValuationRange] = useState<[number, number]>(
    DEFAULT_VALUATION_RANGE
  );
  const [gprRange, setGprRange] =
    useState<[number, number]>(DEFAULT_SCORE_RANGE);
  const [fitScoreRange, setFitScoreRange] =
    useState<[number, number]>(DEFAULT_SCORE_RANGE);
  const [teamStyleFitRange, setTeamStyleFitRange] =
    useState<[number, number]>(DEFAULT_SCORE_RANGE);
  const [carryingRange, setCarryingRange] =
    useState<[number, number]>(DEFAULT_SCORE_RANGE);
  const [dribblingRange, setDribblingRange] =
    useState<[number, number]>(DEFAULT_SCORE_RANGE);
  const [passingRange, setPassingRange] =
    useState<[number, number]>(DEFAULT_SCORE_RANGE);
  const [shootingRange, setShootingRange] =
    useState<[number, number]>(DEFAULT_SCORE_RANGE);
  const [gkPassingRange, setGkPassingRange] =
    useState<[number, number]>(DEFAULT_SCORE_RANGE);
  const [gkShotStoppingRange, setGkShotStoppingRange] =
    useState<[number, number]>(DEFAULT_SCORE_RANGE);
  const [gkPositioningRange, setGkPositioningRange] =
    useState<[number, number]>(DEFAULT_SCORE_RANGE);

  /* --- Custom stat range filter state (scout report ratings, 0-10 scale) --- */
  const [customStatRanges, setCustomStatRanges] = useState<
    Readonly<Record<string, readonly [number, number]>>
  >({});

  const setCustomStatRange = useCallback(
    (name: string, range: [number, number]) => {
      setCustomStatRanges(prev => ({ ...prev, [name]: range }));
    },
    []
  );

  /* --- Filter sheet toggle --- */
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);

  const toggleFilterSheet = useCallback(() => {
    setIsFilterSheetOpen(prev => !prev);
  }, []);

  const closeFilterSheet = useCallback(() => {
    setIsFilterSheetOpen(false);
  }, []);

  /* --- Scout options from query --- */
  const { data: scoutsData, loading: scoutsLoading } =
    useScoutingListScoutsQuery({ skip: skipAll });
  const scoutOptions = useMemo(
    (): readonly FilterOption[] =>
      Object.values(
        (scoutsData?.listScoutingScouts ?? []).reduce(
          (acc, scout) => {
            const label = scout.title ?? scout.email ?? scout.id;
            return { ...acc, [scout.id]: { id: scout.id, label } };
          },
          {} as Record<string, FilterOption>
        )
      ).sort((a, b) => a.label.localeCompare(b.label)),
    [scoutsData?.listScoutingScouts]
  );

  /* --- Role archetype options from query --- */
  const { data: roleData, loading: rolesLoading } =
    useScoutingListRoleArchetypesQuery({
      skip: skipAll,
    });
  const roleOptions = useMemo(
    (): readonly FilterOption[] =>
      [...(roleData?.listRoleArchetypes ?? [])]
        .sort((a, b) => a.localeCompare(b))
        .map(role => ({ id: role, label: role })),
    [roleData?.listRoleArchetypes]
  );

  /* --- Position options from dedicated query --- */
  // `$first` must cover the FULL taxonomy: when omitted the backend returns
  // an arbitrary 20-row default page — on staging Goalkeeper is row 21 and
  // was silently missing from the filter panel (SE-5481).
  const { data: positionData, loading: positionsLoading } =
    useRosterManagementListPositionsQuery({
      variables: { first: POSITIONS_FULL_LIST_FIRST },
      skip: skipAll,
    });
  const positionOptions = useMemo(
    (): readonly FilterOption[] =>
      dedupePositionNodes(positionData?.listPositions?.edges).map(node => ({
        id: node.id,
        label: node.name,
      })),
    [positionData?.listPositions?.edges]
  );

  /* --- Club search text for server-side filtering --- */
  const [clubSearchText, setClubSearchText] = useState("");

  /* --- Club options from broader eligible teams query --- */
  const { data: teamsData, loading: clubSearching } =
    useRosterManagementListMyOrganizationsEligibleTeamsQuery({
      variables: { first: 50, search: clubSearchText || undefined },
      skip: skipAll,
    });
  const rawClubOptions = useMemo(
    (): readonly FilterOption[] =>
      [...(teamsData?.listMyOrganizationsEligibleTeams?.edges ?? [])]
        .map(e => ({ id: e.node.id, label: e.node.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [teamsData?.listMyOrganizationsEligibleTeams?.edges]
  );

  /** Accumulated club labels across search queries for stable chip display */
  const [clubLabelCache, setClubLabelCache] = useState<Record<string, string>>(
    {}
  );

  /** Club options enriched with cached labels for selected clubs not in current search results */
  const clubOptions = useMemo((): readonly FilterOption[] => {
    const currentIds = new Set(rawClubOptions.map(o => o.id));
    const cachedEntries = selectedClubIds
      .filter(id => !currentIds.has(id))
      .map(id => ({ id, label: clubLabelCache[id] ?? id }));
    return cachedEntries.length > 0
      ? [...rawClubOptions, ...cachedEntries].sort((a, b) =>
          a.label.localeCompare(b.label)
        )
      : rawClubOptions;
  }, [rawClubOptions, selectedClubIds, clubLabelCache]);

  /* --- League options from org-scoped curated query --- */
  const { leagueOptions, loading: leaguesLoading } = useOrganizationLeagues({
    skip: skipAll,
  });
  const { competitionOptions, loading: competitionsLoading } =
    useCompetitionOptions({ skip: skipAll });

  /* --- Nationality options from dedicated query --- */
  const { data: nationalitiesData, loading: nationalitiesLoading } =
    useRosterManagementListNationalitiesQuery({
      variables: { first: 1000 },
      skip: skipAll,
    });
  const nationalityGroups = useMemo(
    () =>
      groupNationalitiesByCountry(
        (nationalitiesData?.listNationalities?.edges ?? []).map(e => e.node)
      ),
    [nationalitiesData?.listNationalities?.edges]
  );
  const nationalityOptions = useMemo(
    (): readonly FilterOption[] =>
      nationalityGroups.map(g => ({ id: g.id, label: g.label })),
    [nationalityGroups]
  );
  const nationalityMemberNamesById = useMemo(
    () => buildNationalityMemberNamesById(nationalityGroups),
    [nationalityGroups]
  );

  /**
   * True while the initial filter option queries are still loading.
   * Excludes clubs and leagues because they re-fetch on every search
   * keystroke — gating all chips on their loading state would flash
   * "Loading..." across the entire filter panel during search.
   */
  const optionsLoading =
    scoutsLoading ||
    rolesLoading ||
    positionsLoading ||
    nationalitiesLoading ||
    leaguesLoading ||
    competitionsLoading;

  /* --- Active filter count (prune orphaned custom stat ranges) --- */
  const customStatActiveCount = Object.entries(customStatRanges).filter(
    ([key, range]) =>
      availableMetricIds.has(key) &&
      isRangeActive(range as [number, number], DEFAULT_CUSTOM_STAT_RANGE)
  ).length;

  const rangeFilterCount = countActive(
    isRangeActive(ageRange, DEFAULT_AGE_RANGE),
    isRangeActive(contractMonthsRange, DEFAULT_CONTRACT_MONTHS_RANGE),
    isRangeActive(valuationRange, DEFAULT_VALUATION_RANGE),
    isRangeActive(gprRange, DEFAULT_SCORE_RANGE),
    isRangeActive(fitScoreRange, DEFAULT_SCORE_RANGE),
    isRangeActive(teamStyleFitRange, DEFAULT_SCORE_RANGE),
    isRangeActive(carryingRange, DEFAULT_SCORE_RANGE),
    isRangeActive(dribblingRange, DEFAULT_SCORE_RANGE),
    isRangeActive(passingRange, DEFAULT_SCORE_RANGE),
    isRangeActive(shootingRange, DEFAULT_SCORE_RANGE),
    isRangeActive(gkPassingRange, DEFAULT_SCORE_RANGE),
    isRangeActive(gkShotStoppingRange, DEFAULT_SCORE_RANGE),
    isRangeActive(gkPositioningRange, DEFAULT_SCORE_RANGE)
  );

  const activeFilterCount =
    selectedPositionIds.length +
    selectedClubIds.length +
    selectedScoutIds.length +
    selectedNationalityIds.length +
    selectedLeagueIds.length +
    selectedCompetitionIds.length +
    selectedRoleArchetypes.length +
    rangeFilterCount +
    customStatActiveCount;

  /* --- Clear all filters --- */
  const clearAllFilters = useCallback(() => {
    setClubSearchText("");
    setSelectedPositionIds([]);
    setSelectedClubIds([]);
    setSelectedScoutIds([]);
    setSelectedNationalityIds([]);
    setSelectedLeagueIds([]);
    setSelectedCompetitionIds([]);
    setSelectedRoleArchetypes([]);
    setAgeRange(DEFAULT_AGE_RANGE);
    setContractMonthsRange(DEFAULT_CONTRACT_MONTHS_RANGE);
    setValuationRange(DEFAULT_VALUATION_RANGE);
    setGprRange(DEFAULT_SCORE_RANGE);
    setFitScoreRange(DEFAULT_SCORE_RANGE);
    setTeamStyleFitRange(DEFAULT_SCORE_RANGE);
    setCarryingRange(DEFAULT_SCORE_RANGE);
    setDribblingRange(DEFAULT_SCORE_RANGE);
    setPassingRange(DEFAULT_SCORE_RANGE);
    setShootingRange(DEFAULT_SCORE_RANGE);
    setGkPassingRange(DEFAULT_SCORE_RANGE);
    setGkShotStoppingRange(DEFAULT_SCORE_RANGE);
    setGkPositioningRange(DEFAULT_SCORE_RANGE);
    setCustomStatRanges({});
  }, []);

  /**
   * Restores all 19 filter values from a snapshot — used by
   * the draft filter panel to commit changes on "Show Results"
   */
  const restoreSnapshot = useCallback((snapshot: FilterSnapshot) => {
    /** Merge with defaults to handle persisted snapshots from before new fields were added */
    const safe = { ...DEFAULT_FILTER_SNAPSHOT, ...snapshot };
    setSelectedPositionIds(safe.selectedPositionIds);
    setSelectedClubIds(safe.selectedClubIds);
    setSelectedScoutIds(safe.selectedScoutIds);
    setSelectedNationalityIds(safe.selectedNationalityIds);
    setSelectedLeagueIds(safe.selectedLeagueIds);
    setSelectedCompetitionIds(safe.selectedCompetitionIds);
    setSelectedRoleArchetypes(safe.selectedRoleArchetypes);
    setAgeRange(safe.ageRange);
    setContractMonthsRange(safe.contractMonthsRange);
    setValuationRange(safe.valuationRange);
    setGprRange(safe.gprRange);
    setFitScoreRange(safe.fitScoreRange);
    setTeamStyleFitRange(safe.teamStyleFitRange);
    setCarryingRange(safe.carryingRange);
    setDribblingRange(safe.dribblingRange);
    setPassingRange(safe.passingRange);
    setShootingRange(safe.shootingRange);
    setGkPassingRange(safe.gkPassingRange);
    setGkShotStoppingRange(safe.gkShotStoppingRange);
    setGkPositioningRange(safe.gkPositioningRange);
    setCustomStatRanges(safe.customStatRanges);
  }, []);

  /* --- Side effects: accumulate labels from paginated search results --- */

  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Accumulate labels from paginated club search for stable chip display
    setClubLabelCache(prev =>
      rawClubOptions.reduce(
        (acc, o) => (acc[o.id] === o.label ? acc : { ...acc, [o.id]: o.label }),
        prev
      )
    );
  }, [rawClubOptions]);

  return {
    selectedPositionIds,
    setSelectedPositionIds,
    selectedClubIds,
    setSelectedClubIds,
    selectedScoutIds,
    setSelectedScoutIds,
    selectedNationalityIds,
    setSelectedNationalityIds,
    selectedLeagueIds,
    setSelectedLeagueIds,
    selectedCompetitionIds,
    setSelectedCompetitionIds,
    selectedRoleArchetypes,
    setSelectedRoleArchetypes,
    ageRange,
    setAgeRange,
    contractMonthsRange,
    setContractMonthsRange,
    valuationRange,
    setValuationRange,
    gprRange,
    setGprRange,
    fitScoreRange,
    setFitScoreRange,
    teamStyleFitRange,
    setTeamStyleFitRange,
    carryingRange,
    setCarryingRange,
    dribblingRange,
    setDribblingRange,
    passingRange,
    setPassingRange,
    shootingRange,
    setShootingRange,
    gkPassingRange,
    setGkPassingRange,
    gkShotStoppingRange,
    setGkShotStoppingRange,
    gkPositioningRange,
    setGkPositioningRange,
    customStatRanges,
    setCustomStatRange,
    isFilterSheetOpen,
    toggleFilterSheet,
    closeFilterSheet,
    activeFilterCount,
    clearAllFilters,
    restoreSnapshot,
    scoutOptions,
    roleOptions,
    positionOptions,
    clubOptions,
    leagueOptions,
    competitionOptions,
    competitionOptionsLoading: competitionsLoading,
    nationalityOptions,
    nationalityMemberNamesById,
    setClubSearchText,
    clubSearching,
    optionsLoading,
  };
};

export default useFilterState;
